import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { ReviewCreateWrapper } from '@seedlang/hoc';
import { AppUI } from '@seedlang/state';
import Alert from 'components/alert';
import Button from 'components/button/button';
import ReviewCreateCustom from 'components/review/review_create_custom';
import ReviewCreateWord from 'components/review/review_create_word';
import ReviewImport from 'components/review/review_import';
import Spinner from 'components/spinner';
import Text from 'components/text';
import VocabSentences from 'components/vocab/vocab_sentences';
import { observer } from 'mobx-react';
import React from 'react';
import Toggle from 'react-toggle';

const Wrapper = styled.div`
  border-radius: 5px;
  width: 100%;
  background: white;
`;

const ModeTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin: 0 0 10px 0;
`;

const ModeTabButton = styled.button`
  display: flex;
  align-items: center;
  background: ${props => props.isSelected ? '#EEF7FF' : 'transparent'};
  border: none;
  border-radius: 5px;
  color: ${props => props.isSelected ? Theme.blue : '#333'};
  cursor: pointer;
  font-size: 13px;
  font-weight: bold;
  padding: 8px 10px;
  white-space: nowrap;
`;

const Content = styled.div`
  padding: 10px 10px 20px 10px;
`;

const SentencesWrapper = styled.div`
  margin: 20px 0 0 0;
`;

const Note = styled.div`
  font-size: 13px;
  line-height: 18px;
  margin: 8px 0 0 0;
  color: #666;
`;

const WordNotFoundButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 10px;
`;

const ImportReportTitle = styled.div`
  font-weight: bold;
  margin-bottom: 10px;
`;

const ImportReportSection = styled.div`
  margin-top: ${props => props.marginTop || '10px'};
`;

const ImportReportList = styled.ul`
  margin: 8px 0 0 18px;
  padding: 0;
`;

const ImportReportListItem = styled.li`
  margin: 5px 0 0 0;
`;

const ImportReportReason = styled.span`
  opacity: 0.9;
`;

const OptionText = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LockIcon = styled.i`
  font-size: 13px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  .react-toggle--checked .react-toggle-track{
    background: ${props => props.highlightColor};
  }
`;

@observer
class ReviewCreate extends React.Component {

  render() {
    return (
      <Wrapper
        margin={AppUI.layout.isMobile ? '20px' : '0'}
      >
        <ModeTabs>
          <ModeTabButton
            isSelected={this.props.reviewCreateMode === 'dictionary'}
            onClick={() => this.props.onSetReviewCreateMode('dictionary')}
          >
            <OptionText>
              <i className='fa fa-book' />
              <span>From Dictionary</span>
            </OptionText>
          </ModeTabButton>
          <ModeTabButton
            isSelected={this.props.reviewCreateMode === 'custom'}
            onClick={() => this.props.onSetReviewCreateMode('custom')}
          >
            <OptionText>
              <i className='fa fa-pencil' />
              <span>Custom Review</span>
            </OptionText>
          </ModeTabButton>
          <ModeTabButton
            isSelected={this.props.reviewCreateMode === 'import'}
            onClick={() => this.props.onSetReviewCreateMode('import')}
          >
            <OptionText>
              <i className='fa fa-upload' />
              <span>Import</span>
              {
                !this.props.canImportReviews &&
                <LockIcon
                  className='fa fa-lock'
                  style={{
                    color: this.props.reviewCreateMode === 'import' ? Theme.blue : '#9CA3AF',
                  }}
                />
              }
            </OptionText>
          </ModeTabButton>
        </ModeTabs>
        <Content>
          {
            this.props.reviewImportReport &&
            <Alert
              state={this.props.reviewImportReport.importedCount > 0 ? 'success' : 'failure'}
              margin='0 0 20px 0'
              showCloseButton
              onClose={this.props.removeReviewImportReport}
            >
              <ImportReportTitle>
                Import Report
              </ImportReportTitle>
              <div>
                Imported {this.props.reviewImportReport.importedCount} {this.props.reviewImportReport.importedCount === 1 ? 'review' : 'reviews'}.
              </div>
              <ImportReportSection>
                Created by card type
                {
                  this.props.reviewImportReport.createdCardTypeCounts.length > 0 &&
                  <ImportReportList>
                    {
                      this.props.reviewImportReport.createdCardTypeCounts.map(item => (
                        <ImportReportListItem key={item.cardType}>
                          {item.count} {item.cardType} {item.count === 1 ? 'review' : 'reviews'}
                        </ImportReportListItem>
                      ))
                    }
                  </ImportReportList>
                }
                {
                  this.props.reviewImportReport.createdCardTypeCounts.length === 0 &&
                  <div>
                    No reviews were created.
                  </div>
                }
              </ImportReportSection>
              {
                this.props.reviewImportReport.skippedCount === 0 &&
                <ImportReportSection marginTop='14px'>
                  All reviews were successfully created.
                </ImportReportSection>
              }
              {
                this.props.reviewImportReport.skippedCount > 0 &&
                <ImportReportSection marginTop='14px'>
                  Couldn't create {this.props.reviewImportReport.skippedCount} {this.props.reviewImportReport.skippedCount === 1 ? 'review' : 'reviews'}.
                  {
                    this.props.reviewImportReport.failedCards.length > 0 &&
                    <ImportReportList>
                      {
                        this.props.reviewImportReport.failedCards.map((item, index) => (
                          <ImportReportListItem key={`${item.label}-${item.reason}-${index}`}>
                            {item.label}
                            {
                              item.reason &&
                              <ImportReportReason>
                                : {item.reason}
                              </ImportReportReason>
                            }
                          </ImportReportListItem>
                        ))
                      }
                    </ImportReportList>
                  }
                </ImportReportSection>
              }
            </Alert>
          }
          {
            this.props.reviewCreateMode === 'dictionary' &&
            <ReviewCreateWord
              {...this.props}
            />
          }
          {
            this.props.reviewCreateMode === 'custom' &&
            <ReviewCreateCustom
              {...this.props}
            />
          }
          {
            this.props.reviewCreateMode === 'import' &&
            <ReviewImport
              canImportReviews={this.props.canImportReviews}
              importFileInputKey={this.props.importFileInputKey}
              onSetImportFile={this.props.onSetImportFile}
              importFile={this.props.importFile}
              importCardType={this.props.importCardType}
              importSeparator={this.props.importSeparator}
              importHasHeaderRow={this.props.importHasHeaderRow}
              reviewCreatedMessage={this.props.reviewCreatedMessage}
              onSetImportCardType={this.props.onSetImportCardType}
              onSetImportSeparator={this.props.onSetImportSeparator}
              onSetImportHasHeaderRow={this.props.onSetImportHasHeaderRow}
              reviewDeckOptions={this.props.reviewDeckOptions}
              deckId={this.props.deckId}
              onSetDeckId={this.props.onSetDeckId}
              onSubmit={this.props.onSubmit}
              onCancel={this.props.onCancel}
              disableSubmit={this.props.disableSubmit}
              disableCancel={this.props.disableCancel}
              showSpinner={this.props.showSpinner}
            />
          }
          {
            this.props.showWordNotFound &&
            <WordNotFoundButtons>
              <Button
                backgroundOnActive={Theme.blueGray}
                margin='0 5px 0 0'
                onClick={this.props.onCreateWord}
              >
                <i
                  className='fa fa-pencil'
                  style={{ marginRight: 5, color: 'white' }}
                />
                Create Custom Review
              </Button>
              <Button
                onClick={this.props.clearWordSearch}
                background={Theme.red}
                margin='0'
              >
                <i
                  className='fa fa-times'
                  style={{ marginRight: 5, color: 'white' }}
                />
                Cancel
              </Button>
            </WordNotFoundButtons>
          }
          {
            !this.props.showWordNotFound && this.props.reviewCreateMode !== 'import' &&
            !this.props.disableSubmit &&
            !this.props.showSpinner &&
            <Button
              onClick={() => this.props.onSubmit(null)}
              backgroundOnActive={Theme.blueGray}
              margin='20px 5px 0 0'
            >
              <i
                className='fa fa-check'
                style={{ marginRight: 5, color: 'white' }}
              />
              Create Review
            </Button>
          }
          {
            !this.props.showWordNotFound &&
            this.props.reviewCreateMode !== 'import' &&
            !this.props.disableCancel &&
            !this.props.showSpinner &&
            <Button
              onClick={() => this.props.onCancel({
                preserveWordSearch: Boolean(this.props.reviewCreateMode === 'dictionary' && this.props.word),
              })}
              background={Theme.red}
              margin='20px 0 0 0'
            >
              <i
                className='fa fa-times'
                style={{ marginRight: 5, color: 'white' }}
              />
              Cancel
            </Button>
          }
          {
            this.props.reviewCreatedMessage && this.props.reviewCreateMode !== 'import' &&
            <Alert state='success' margin='20px 0 0 0'>
              {this.props.reviewCreatedMessage}
            </Alert>
          }
          {
            this.props.showSpinner &&
            <Spinner
              margin='20px 0 0 10px'
            />
          }
          {
            this.props.reviewCreateMode === 'custom' &&
            <div>
              <ToggleWrapper
                highlightColor={Theme.green}
              >
                <Toggle
                  icons={false}
                  onChange={this.props.toggleConvertCardSubmissions}
                  checked={AppUI.user['convertCardSubmissions']}
                />
                <Text
                  fontSize='12px'
                  bold
                  margin='0 0 0 10px'
                >
                  Convert Custom Reviews
                </Text>
              </ToggleWrapper>
              <Note>
                When words from your custom cards are added to our dictionary and recorded, the cards will be converted to word translation cards.
              </Note>
            </div>
          }
          {
            this.props.word && this.props.word.id && this.props.showAddWord &&
            <SentencesWrapper>
              <Text
                bold
                margin='0 0 10px 0'
              >
                Sentences
              </Text>
              <VocabSentences
                wordId={this.props.word.id}
                onClickSentence={this.props.onClickSentence}
              />
            </SentencesWrapper>
          }
        </Content>
      </Wrapper>
    );
  }
}

export default ReviewCreateWrapper(ReviewCreate);
